import { useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../../api/reservations";
import type { SchemaRegion } from "../../../../api/restaurantSchema";
import { CloseIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { LIST_TABS, groupByTab, matchesSearch, type ListTab } from "../../logic/reservationList";
import { canRestore, canUnseat, isSeatShown, seatHintOf, type UpcomingActions } from "../../logic/reservationActions";
import type { ReservationActions } from "../../hooks/useReservationActions";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import type { MomentFocus } from "../../logic/momentFocus";
import ReservationCard, { type CardAction } from "./ReservationCard";

// След колко мс спокойствие на плъзгача списъкът се превърта до първата
// резервация на масите (не на всяка стъпка по време на плъзгане).
const MOMENT_SCROLL_DELAY_MS = 350;

/**
 * Дясната колона — списъкът: табове „Предстоящи N / Настанени N / Минали N",
 * търсачка „Име, телефон или маса" и картите. Кликът на карта фокусира
 * масата ѝ на платното; подчертаната карта се превърта във видимата част.
 * В режим „Преглед към" (`momentFocus`) резервациите на масите в избрания
 * момент са оградени в оранжево, пристигащите до час — нормални, а
 * останалите — приглушени; табът с такива резервации носи точка, а
 * списъкът се превърта до първата от тях, щом плъзгачът спре.
 */
const ReservationsList = ({
  reservations,
  regions,
  activeRegionId,
  tab,
  selectedReservationId,
  nowMs,
  isNarrow,
  stacked,
  schemaHidden,
  actions,
  upcomingActionsOf,
  isLoading,
  onTabChange,
  onSelect,
  onEdit,
  momentFocus,
}: {
  reservations: Reservation[];
  regions: SchemaRegion[];
  activeRegionId: number | null;
  tab: ListTab;
  selectedReservationId: number | null;
  nowMs: number;
  isNarrow: boolean;
  // Колоната е под платното: без вътрешен скрол.
  stacked: boolean;
  // Схемата не се показва (телефон) — поповърът на масата го няма.
  schemaHidden: boolean;
  actions: ReservationActions;
  // Кои действия има предстоящата (ден, маси, заетост).
  upcomingActionsOf: (reservation: Reservation) => UpcomingActions | null;
  // Резервациите на току-що избрания ден още се зареждат.
  isLoading: boolean;
  onTabChange: (tab: ListTab) => void;
  onSelect: (reservation: Reservation) => void;
  // „Редактирай" на предстояща — дясната колона става формата.
  onEdit: (reservation: Reservation) => void;
  // Избраният на линията момент, различен от „сега" (null — на живо).
  momentFocus: MomentFocus | null;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const [query, setQuery] = useState("");
  const cardRefs = useRef(new Map<number, HTMLDivElement>());

  const groups = useMemo(() => groupByTab(reservations), [reservations]);
  const visible = groups[tab].filter((reservation) => matchesSearch(reservation, query));

  const regionOfTable = useMemo(() => {
    const map = new Map<number, SchemaRegion>();
    for (const region of regions) {
      for (const element of region.elements) if (element.kind === "table") map.set(element.id, region);
    }
    return map;
  }, [regions]);

  // Подчертаната карта (клик на маса, нова резервация) — във видимата част.
  // Превърта се веднъж при смяна на избора — и когато картата се появи чак
  // след опресняването (новосъздадена), но не при всяко опресняване.
  const pendingScrollId = useRef<number | null>(null);
  useEffect(() => {
    pendingScrollId.current = selectedReservationId;
  }, [selectedReservationId, tab]);
  useEffect(() => {
    // Под платното (stacked) превъртането е към платното — в Reservations.
    const targetId = pendingScrollId.current;
    if (targetId === null || stacked) return;
    const card = cardRefs.current.get(targetId);
    if (!card) return;
    card.scrollIntoView({ block: "nearest", behavior: "smooth" });
    pendingScrollId.current = null;
  }, [selectedReservationId, tab, reservations, stacked]);

  // Прегледът: щом плъзгачът спре, първата карта на масите (или първата
  // пристигаща) във видимия таб — във видимата част. Подчертаната карта
  // (клик) има предимство.
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const focusRef = useRef(momentFocus);
  focusRef.current = momentFocus;
  const momentKey = momentFocus?.momentMs ?? null;
  useEffect(() => {
    if (momentKey === null || stacked) return;
    const timer = window.setTimeout(() => {
      const focus = focusRef.current;
      if (!focus || pendingScrollId.current !== null) return;
      const target =
        visibleRef.current.find((reservation) => focus.roles.get(reservation.id) === "active") ??
        visibleRef.current.find((reservation) => focus.roles.get(reservation.id) === "soon");
      if (target) cardRefs.current.get(target.id)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, MOMENT_SCROLL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [momentKey, tab, stacked]);

  /**
   * „Маса 1", няколко — „Маса 1, 2"; друг регион — „Маса 10 · Тераса"; масите
   * изтрити от схемата — „Без маса".
   */
  const tableLabelOf = (reservation: Reservation) => {
    if (reservation.tableNames.length === 0) return t("reservations.list.noTable");
    const names = reservation.tableNames.join(", ");
    const table = format.tableName(names);
    const region = reservation.tableIds.map((id) => regionOfTable.get(id)).find(Boolean);
    return region && region.id !== activeRegionId
      ? t("reservations.list.tableInRegion", { table, region: region.name })
      : table;
  };

  const isPendingFor = (reservation: Reservation, action: string) =>
    actions.isPending && actions.pendingAction === action && actions.pendingId === reservation.id;

  /** Обяснението на „Настани": защо е изключен, към чия сметка ще се закачи или че ще питаме. */
  const seatHint = (available: UpcomingActions) => {
    const hint = seatHintOf(available);
    return hint ? t(hint.key, hint.params) : undefined;
  };

  /**
   * Бутоните на картата: настанената — „Тръгнаха си" + иконата „Отмени
   * настаняването"; отказаната — „Върни"; предстоящата —
   * „Настани" (без него за друг ден/без маса; изключен при маса на друга
   * настанена резервация и по-рано от 30 мин преди часа) + иконата „Редактирай".
   * Резервация без маса на платното или при скрита схема (телефон) няма
   * поповър → „Откажи" е на самата карта, иначе няма как да се махне от
   * „Предстоящи".
   */
  const actionsOf = (reservation: Reservation): CardAction[] => {
    const disabled = actions.isPending;
    const pending = (action: string) => isPendingFor(reservation, action);
    if (reservation.status === "seated") {
      const release: CardAction = {
        kind: "release",
        disabled,
        pending: pending("release"),
        onClick: () => void actions.release(reservation),
      };
      // След края на часа „Отмени настаняването" го няма (BE → 409).
      if (!canUnseat(reservation, nowMs)) return [release];
      return [
        release,
        { kind: "unseat", disabled, pending: pending("unseat"), onClick: () => void actions.unseat(reservation) },
      ];
    }
    if (canRestore(reservation)) {
      return [{ kind: "restore", disabled, pending: pending("restore"), onClick: () => void actions.restore(reservation) }];
    }
    const available = upcomingActionsOf(reservation);
    if (!available) return [];
    const result: CardAction[] = [];
    if (isSeatShown(available.seatBlock)) {
      result.push({
        kind: "seat",
        disabled: disabled || available.seatBlock !== null,
        hint: seatHint(available),
        pending: pending("seat"),
        onClick: () => void actions.seat(reservation),
      });
    }
    const onCanvas = reservation.tableIds.some((id) => regionOfTable.has(id));
    if (!onCanvas || schemaHidden) {
      result.push({ kind: "cancel", disabled, pending: pending("cancel"), onClick: () => void actions.cancel(reservation) });
    }
    if (available.canEdit) {
      result.push({ kind: "edit", disabled, pending: false, onClick: () => onEdit(reservation) });
    }
    return result;
  };

  return (
    <div
      className={`flex flex-col rounded-[14px] border border-shade-2 bg-surface ${
        stacked ? "" : "min-h-0 flex-1 overflow-hidden"
      }`}
    >
      <div className="grid shrink-0 grid-cols-3 border-b border-shade-2 px-2" role="tablist">
        {LIST_TABS.map((item) => {
          const active = item === tab;
          return (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onTabChange(item)}
              className={`-mb-px flex h-12 cursor-pointer items-center justify-center gap-1.5 border-x-0 border-b-2 border-t-0 bg-transparent px-1 text-[13.5px] font-bold transition-colors duration-150 ${
                active ? "border-brand text-ink" : "border-transparent text-body hover:text-ink"
              }`}
            >
              {t(`reservations.list.tabs.${item}`)}
              {/* Точката „активни в момента" е закачена горе вдясно на брояча
                  (absolute) — да не разширява етикета и табовете да не трепват
                  при влизане/излизане от прегледа. */}
              <span className={`relative text-[12px] tabular-nums ${active ? "text-brand-deep-ink" : "text-muted"}`}>
                {groups[item].length}
                {momentFocus && momentFocus.activeByTab[item] > 0 && (
                  <span
                    title={t("reservations.list.moment.tabHasActive", { time: momentFocus.clock })}
                    className="absolute -right-2 top-0 size-1.5 rounded-full bg-brand"
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="shrink-0 px-3 pt-3">
        <label className="relative flex items-center">
          <SearchOutlined className="pointer-events-none absolute left-3.5 text-[13px] text-muted" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("reservations.list.search")}
            aria-label={t("reservations.list.search")}
            className={`h-10 w-full rounded-[10px] border border-shade-3 bg-surface pl-9 pr-9 font-medium text-ink outline-none transition-colors duration-150 placeholder:text-faint focus:border-brand [&::-webkit-search-cancel-button]:hidden ${
              isNarrow ? "text-[16px]" : "text-[13.5px]"
            }`}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("reservations.list.clearSearch")}
              className="absolute right-2 flex size-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
            >
              <CloseIcon className="size-3" />
            </button>
          )}
        </label>
      </div>

      <div className={`flex flex-col gap-2.5 p-3 ${stacked ? "" : "min-h-0 flex-1 overflow-y-auto"}`}>
        {visible.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] font-medium text-muted">
            {isLoading
              ? t("common.loading")
              : query.trim()
                ? t("reservations.list.noMatches")
                : t(`reservations.list.empty.${tab}`)}
          </div>
        ) : (
          visible.map((reservation) => {
            const role = momentFocus?.roles.get(reservation.id) ?? null;
            const selected = reservation.id === selectedReservationId;
            return (
              <div
                key={reservation.id}
                // Картата си има своя черта вляво (закъснява/пристига) —
                // прегледът я огражда, за да не се бият двата сигнала.
                className={`relative shrink-0 rounded-[12px] transition-[opacity,box-shadow] duration-200 ${
                  role === "active" ? "ring-2 ring-brand/45" : ""
                } ${role === "idle" && !selected ? "opacity-45 hover:opacity-100" : ""}`}
              >
                <ReservationCard
                  ref={(element) => {
                    if (element) cardRefs.current.set(reservation.id, element);
                    else cardRefs.current.delete(reservation.id);
                  }}
                  reservation={reservation}
                  tableLabel={tableLabelOf(reservation)}
                  nowMs={nowMs}
                  selected={selected}
                  actions={actionsOf(reservation)}
                  onSelect={() => onSelect(reservation)}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ReservationsList;
